<script setup lang="ts">
import { nextTick, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Button from "../components/Button.vue";
import Icon from "../components/Icon.vue";
import { login, loginWithCode, openWorkspace } from "../composables/session";
import { api, ApiError } from "../api/client";
import { errorText } from "../api/errors";
import type { Workspace } from "../api/types";
import { t } from "../i18n";
const router = useRouter();
const route = useRoute();
const email = ref("");
const password = ref("");
const error = ref("");
const busy = ref(false);
/** After the right password, when the person turned two-step verification on. */
const challenge = ref<string | null>(null);
const code = ref("");
const useRecovery = ref(false);
const forgot = ref(false);
const forgotSent = ref(false);
/** A one-hour link by e-mail; the answer is the same whether or not the address has an account. */
async function sendReset() {
  error.value = "";
  if (!email.value.trim()) {
    error.value = t("auth.forgot_email_first");
    return;
  }
  busy.value = true;
  try {
    await api("/v1/auth/password/forgot", {
      method: "POST",
      body: { email: email.value.trim() },
    });
    forgotSent.value = true;
  } catch (err) {
    error.value = failure(err);
  } finally {
    busy.value = false;
  }
}
const codeInput = ref<HTMLInputElement>();

function go(workspaces: Workspace[]) {
  const next = typeof route.query.next === "string" ? route.query.next : null;
  const ready = workspaces.filter((w) => w.ready);
  if (next) return router.replace(next);
  if (ready.length === 1) {
    openWorkspace(ready[0]!.slug);
    return router.replace("/inbox");
  }
  return router.replace("/workspaces");
}

function failure(err: unknown): string {
  if (err instanceof ApiError && err.code === "invalid_credentials")
    return t("auth.invalid");
  if (err instanceof ApiError && err.code === "user_blocked")
    return t("auth.blocked");
  return errorText(err);
}

async function submit() {
  error.value = "";
  busy.value = true;
  try {
    const outcome = await login(email.value.trim(), password.value);
    if ("challenge" in outcome) {
      challenge.value = outcome.challenge;
      await nextTick();
      codeInput.value?.focus();
      return;
    }
    await go(outcome.workspaces);
  } catch (err) {
    error.value = failure(err);
  } finally {
    busy.value = false;
  }
}

async function submitCode() {
  if (!challenge.value) return;
  error.value = "";
  busy.value = true;
  try {
    await go(await loginWithCode(challenge.value, code.value.trim()));
  } catch (err) {
    // An expired ticket means the password again.
    if (err instanceof ApiError && err.code === "mfa_challenge_expired") {
      challenge.value = null;
      code.value = "";
      password.value = "";
    }
    error.value = failure(err);
  } finally {
    busy.value = false;
  }
}

function back() {
  challenge.value = null;
  code.value = "";
  useRecovery.value = false;
  error.value = "";
}
</script>
<template>
  <div class="auth-page">
    <form v-if="!challenge" class="auth-card" @submit.prevent="submit">
      <div class="auth-brand">
        <Icon name="chat" :size="28" /><span>{{ t("app.name") }}</span>
      </div>
      <h1>{{ t("auth.title") }}</h1>
      <label class="wa-field"
        ><span>{{ t("auth.email") }}</span
        ><input
          v-model="email"
          type="email"
          autocomplete="username"
          required
          autofocus
      /></label>
      <label class="wa-field"
        ><span>{{ t("auth.password") }}</span
        ><input
          v-model="password"
          type="password"
          autocomplete="current-password"
          required
      /></label>
      <p v-if="error" class="auth-error" role="alert">{{ error }}</p>
      <Button type="submit" :loading="busy">{{ t("auth.submit") }}</Button>
      <button type="button" class="auth-link" @click="forgot = !forgot">
        {{ t("auth.forgot") }}
      </button>
      <div v-if="forgot" class="auth-help">
        <p class="small muted">
          {{
            forgotSent
              ? t("auth.forgot_sent", { email: email.trim() })
              : t("auth.forgot_lead")
          }}
        </p>
        <Button
          v-if="!forgotSent"
          type="button"
          variant="secondary"
          :loading="busy"
          @click="sendReset"
          >{{ t("auth.forgot_send") }}</Button
        >
        <p class="small muted">{{ t("auth.forgot_help") }}</p>
      </div>
    </form>
    <form v-else class="auth-card" @submit.prevent="submitCode">
      <div class="auth-brand">
        <Icon name="shield" :size="28" /><span>{{ t("app.name") }}</span>
      </div>
      <h1>{{ t("auth.mfa.title") }}</h1>
      <p class="small muted">
        {{ useRecovery ? t("auth.mfa.recovery_lead") : t("auth.mfa.lead") }}
      </p>
      <label class="wa-field"
        ><span>{{
          useRecovery ? t("auth.mfa.recovery_code") : t("auth.mfa.code")
        }}</span
        ><input
          ref="codeInput"
          v-model="code"
          :inputmode="useRecovery ? 'text' : 'numeric'"
          :autocomplete="useRecovery ? 'off' : 'one-time-code'"
          :maxlength="useRecovery ? 11 : 6"
          :placeholder="useRecovery ? 'XXXXX-XXXXX' : '000000'"
          class="auth-code"
          required
      /></label>
      <p v-if="error" class="auth-error" role="alert">{{ error }}</p>
      <Button type="submit" :loading="busy">{{ t("auth.mfa.submit") }}</Button>
      <button
        type="button"
        class="auth-link"
        @click="((useRecovery = !useRecovery), (code = ''))"
      >
        {{ useRecovery ? t("auth.mfa.use_app") : t("auth.mfa.use_recovery") }}
      </button>
      <button type="button" class="auth-link muted" @click="back">
        {{ t("auth.mfa.back") }}
      </button>
    </form>
  </div>
</template>
